/**
 * Clip position model.
 *
 * Audio is not musical time. A recording sits at a fixed point in SECONDS —
 * change the project tempo and it must not slide. So audio clips carry sample
 * positions as their source of truth; musical (pattern) clips stay in beats,
 * where being tempo-relative is exactly what you want.
 *
 * Sample positions are stored as DECIMAL STRINGS rather than BigInt. Clips are
 * JSON-serialised constantly (persistence, WebSocket broadcasts, undo
 * snapshots) and JSON.stringify throws on BigInt; strings are exact, safe
 * everywhere, and parse back losslessly.
 *
 * Beat fields are kept alongside as DERIVED values so existing readers (the
 * scheduler, collaborators on older payloads, pattern-clip code) keep working.
 * They are computed without tick quantisation, so they stay faithful.
 */

import { toBigInt, sampleToBeatExact, beatToSampleExact } from './timebase';

export const SAMPLE_POSITION_FIELDS = [
  'timelineStartSample',
  'lengthSamples',
  'sourceStartSample'
];

/** Parse a stored sample field (string | number | bigint) into BigInt. */
export function parseSample(value, fallback = 0n) {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? BigInt(Math.round(value)) : fallback;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!/^-?\d+$/.test(trimmed)) return fallback;
    try { return BigInt(trimmed); } catch (_) { return fallback; }
  }
  return fallback;
}

/** Serialise a BigInt sample position for storage/transport. */
export const formatSample = (sample) => toBigInt(sample).toString();

/** True when this clip already carries authoritative sample positions. */
export const isMusicalClip = (clip) => clip?.clipType === 'pattern' || clip?.tempoMatched === true;

export const isSampleAuthoritative = (clip) => (
  !isMusicalClip(clip) &&
  clip?.timelineStartSample !== undefined && clip?.timelineStartSample !== null
);

/**
 * Timeline start in samples. Prefers the authoritative sample field and falls
 * back to the legacy beat position, so pre-migration clips still resolve.
 */
export function getTimelineStartSample(clip, tempoMap) {
  if (isSampleAuthoritative(clip)) {
    const parsed = parseSample(clip.timelineStartSample, null);
    if (parsed !== null) return parsed < 0n ? 0n : parsed;
  }
  return beatToSampleExact(Math.max(0, Number(clip?.startBeat) || 0), tempoMap);
}

/** Clip length in samples, measured end-minus-start so tempo maps apply. */
export function getLengthSamples(clip, tempoMap) {
  if (!isMusicalClip(clip) && clip?.lengthSamples !== undefined && clip?.lengthSamples !== null) {
    const parsed = parseSample(clip.lengthSamples, null);
    if (parsed !== null) return parsed < 0n ? 0n : parsed;
  }
  const start = Math.max(0, Number(clip?.startBeat) || 0);
  const duration = Math.max(0, Number(clip?.durationBeats) || 0);
  const length = beatToSampleExact(start + duration, tempoMap) - beatToSampleExact(start, tempoMap);
  return length > 0n ? length : 0n;
}

/** Offset into the source buffer where this clip begins playing. */
export function getSourceStartSample(clip, tempoMap) {
  if (!isMusicalClip(clip) && clip?.sourceStartSample !== undefined && clip?.sourceStartSample !== null) {
    const parsed = parseSample(clip.sourceStartSample, null);
    if (parsed !== null) return parsed < 0n ? 0n : parsed;
  }
  // Legacy clips express the source offset as beats into the buffer.
  return beatToSampleExact(Math.max(0, Number(clip?.audioOffset) || 0), tempoMap);
}

/**
 * Build a position patch. Sample fields are authoritative; beat fields are
 * derived (exactly, without tick rounding) so every existing reader stays
 * correct. Only the provided components are written.
 */
export function positionPatch({ startSample, lengthSamples, sourceStartSample }, tempoMap) {
  const patch = {};

  if (startSample !== undefined && startSample !== null) {
    const start = toBigInt(startSample);
    const safeStart = start < 0n ? 0n : start;
    patch.timelineStartSample = formatSample(safeStart);
    patch.startBeat = sampleToBeatExact(safeStart, tempoMap);
  }

  if (lengthSamples !== undefined && lengthSamples !== null) {
    const length = toBigInt(lengthSamples);
    const safeLength = length < 0n ? 0n : length;
    patch.lengthSamples = formatSample(safeLength);
    // durationBeats is a span, so derive it against this clip's own start.
    const startRef = patch.timelineStartSample !== undefined
      ? parseSample(patch.timelineStartSample)
      : 0n;
    patch.durationBeats = sampleToBeatExact(startRef + safeLength, tempoMap)
      - sampleToBeatExact(startRef, tempoMap);
  }

  if (sourceStartSample !== undefined && sourceStartSample !== null) {
    const source = toBigInt(sourceStartSample);
    const safeSource = source < 0n ? 0n : source;
    patch.sourceStartSample = formatSample(safeSource);
    patch.audioOffset = sampleToBeatExact(safeSource, tempoMap);
  }

  return patch;
}

/**
 * Give a legacy (beat-only) audio clip authoritative sample positions,
 * derived from its current beats. Non-audio clips are returned untouched —
 * pattern clips are musical and stay tick/beat based by design.
 */
export function migrateClipToSamples(clip, tempoMap) {
  if (!clip || isMusicalClip(clip)) return clip;
  if (isSampleAuthoritative(clip)) return clip;

  return {
    ...clip,
    timelineStartSample: formatSample(getTimelineStartSample(clip, tempoMap)),
    lengthSamples: formatSample(getLengthSamples(clip, tempoMap)),
    sourceStartSample: formatSample(getSourceStartSample(clip, tempoMap))
  };
}

/** Migrate a whole clip list (session load). */
export const migrateClipsToSamples = (clips, tempoMap) => (
  (Array.isArray(clips) ? clips : []).map((clip) => migrateClipToSamples(clip, tempoMap))
);

/**
 * Re-anchor a clip after a tempo change.
 *
 * Audio clips keep their SAMPLE position — a recording lives at a fixed point
 * in time and must not slide when the project tempo changes — and their beat
 * fields are re-derived against the new tempo. Pattern clips are musical, so
 * they are left alone and follow the tempo by design.
 */
export function rebaseClipToTempo(clip, previousTempoMap, nextTempoMap) {
  if (!clip || isMusicalClip(clip)) return clip;

  const anchored = migrateClipToSamples(clip, previousTempoMap);
  return {
    ...anchored,
    ...positionPatch({
      startSample: parseSample(anchored.timelineStartSample),
      lengthSamples: parseSample(anchored.lengthSamples),
      sourceStartSample: parseSample(anchored.sourceStartSample)
    }, nextTempoMap)
  };
}

export const rebaseClipsToTempo = (clips, previousTempoMap, nextTempoMap) => (
  (Array.isArray(clips) ? clips : []).map(
    (clip) => rebaseClipToTempo(clip, previousTempoMap, nextTempoMap)
  )
);

// ── Collaboration wire format ──────────────────────────────────────────────
//
// Clip positions cross the wire as decimal strings. A payload carries the
// authoritative sample position AND the derived beat, so peers running older
// code keep working while current peers stay sample-exact.

/** Build the position half of a clip-move broadcast. */
export function clipMovePayload(clip) {
  return {
    timelineStartSample: clip?.timelineStartSample ?? null,
    newStartBeat: Number(clip?.startBeat) || 0,
    durationBeats: Number.isFinite(Number(clip?.durationBeats))
      ? Number(clip.durationBeats)
      : null,
    lengthSamples: clip?.lengthSamples ?? null
  };
}

/**
 * Turn a received clip-move payload into a position patch.
 *
 * Prefers the authoritative sample field. A legacy beat-only payload is
 * converted to samples here rather than written straight through — otherwise
 * the receiver would end up with a new startBeat next to a stale
 * timelineStartSample, and readers (which prefer samples) would show the clip
 * at its OLD position.
 */
export function remoteMovePatch(payload, tempoMap) {
  const sample = payload?.timelineStartSample;
  const lengthSamples = payload?.lengthSamples;
  const durationBeats = Number(payload?.durationBeats);
  const durationPatch = Number.isFinite(durationBeats) ? { durationBeats } : {};
  if (sample !== undefined && sample !== null && sample !== '') {
    return {
      ...positionPatch({
        startSample: parseSample(sample),
        ...(lengthSamples !== undefined && lengthSamples !== null && lengthSamples !== ''
          ? { lengthSamples: parseSample(lengthSamples) }
          : {})
      }, tempoMap),
      ...durationPatch
    };
  }
  const beat = Math.max(0, Number(payload?.newStartBeat) || 0);
  return {
    ...positionPatch({ startSample: beatToSampleExact(beat, tempoMap) }, tempoMap),
    ...durationPatch
  };
}

/** Stable dedup key for an incoming move (sample when available). */
export const remoteMoveKey = (payload) => (
  payload?.timelineStartSample != null
    ? `s:${payload.timelineStartSample}`
    : `b:${Number(payload?.newStartBeat) || 0}`
);
